import { useRef, useContext, ReactNode, memo } from 'react';
import { useFieldArray } from 'react-hook-form';

import { useLanguage } from '@languages/index';
import TextInputField from 'components/textInput';
import CampaignFormField, {
  CampaignFormFieldProps,
} from '../../../../campaignFormFieldGroup/components/campaignFormField/campaignFormField';
import { CampaignFormContext } from 'pages/campaigns/campaignForm/campaignForm.context';
import useUploadGuidelineImage from 'hooks/useUploadGuidelineImage/useUploadGuidelineImage';

// 동적 색상이 가능한 SVG 아이콘 컴포넌트
const ArrowUpIcon = ({ color = "black", className = "w-[24px] h-[24px]" }: { color?: string; className?: string }) => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
    <rect x="0.5" y="0.5" width="23" height="23" fill="white" stroke="#E8E8E8"/>
    <path d="M8 14L12 10L16 14" stroke={color} strokeWidth="1.5"/>
  </svg>
);

const ArrowDownIcon = ({ color = "black", className = "w-[24px] h-[24px]" }: { color?: string; className?: string }) => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
    <rect x="23.5" y="23.5" width="23" height="23" transform="rotate(180 23.5 23.5)" fill="white" stroke="#E8E8E8"/>
    <path d="M16 10L12 14L8 10" stroke={color} strokeWidth="1.5"/>
  </svg>
);
interface GuideImageProps
  extends Pick<CampaignFormFieldProps, 'label' | 'labelClassName' | 'description' | 'descriptionClassName'> {
  uploadButtonText: ReactNode;
}

const GuideImageField = ({
  label,
  labelClassName,
  description,
  descriptionClassName,
  uploadButtonText,
}: GuideImageProps) => {
  const language = useLanguage();
  const languageTexts = {
    ...language.common,
    ...language.pages.campaigns,
  };
  const { modal, guidelineRuleFormMethods } = useContext(CampaignFormContext);

  const guideImage = useFieldArray({
    control: guidelineRuleFormMethods.control,
    name: 'GUIDE_IMAGE',
    // react-hook-form id 값
    keyName: '_reactHookFormId',
  });

  const fileInputRef = useRef<HTMLInputElement>(null);
  const replaceFileInputRefs = useRef<{ [key: number]: HTMLInputElement | null }>({});

  const { mutate: uploadGuideImageMutate, isPending: isUploadingGuideImage } = useUploadGuidelineImage();

  const onClickUploadGuideImageButton = async () => {
    await new Promise((resolve) => {
      modal.showModal({
        hideCloseButton: true,
        title: languageTexts.create_reels_form_image_guideline_beauty_modal_title,
        text: languageTexts.create_reels_form_image_guideline_beauty_modal_description,
        onConfirm: resolve,
        onRequestClose: resolve,
      });
    });

    if (!isUploadingGuideImage) {
      fileInputRef.current.value = '';
      fileInputRef.current?.click();
    }
  };

  const onChangeGuideImage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const image = event.target.files?.[0];

    if (image) {
      uploadGuideImageMutate(image, {
        onSuccess: (data) => {
          guideImage.append({
            value: JSON.stringify({
              url: data.data.url,
              description: '',
            }),
          });
        },
        onError: (error: Error) => {
          modal.showModal({
            text: error?.message,
          });
        },
      });
    }
  };

  const onReplaceGuideImage = async (index: number) => {
    await new Promise((resolve) => {
      modal.showModal({
        hideCloseButton: true,
        title: languageTexts.create_reels_form_image_guideline_beauty_modal_title,
        text: languageTexts.create_reels_form_image_guideline_beauty_modal_description,
        onConfirm: resolve,
        onRequestClose: resolve,
      });
    });

    if (!isUploadingGuideImage) {
      replaceFileInputRefs.current[index]?.click();
    }
  };

  const onChangeReplaceGuideImage = (event: React.ChangeEvent<HTMLInputElement>, index: number) => {
    const image = event.target.files?.[0];

    if (image) {
      uploadGuideImageMutate(image, {
        onSuccess: (data) => {
          const field = guideImage.fields[index];
          const { description } = JSON.parse(field.value as string);

          guideImage.update(index, {
            id: field.id,
            order: field.order ?? index + 1,
            value: JSON.stringify({
              url: data.data.url,
              description: description || '',
            }),
          });
        },
        onError: (error: Error) => {
          modal.showModal({
            text: error?.message,
          });
        },
      });
    }

    // Reset input value
    if (event.target) {
      event.target.value = '';
    }
  };

  const onDeleteGuideImage = (index: number) => {
    modal.showModal({
      text: languageTexts.create_delete_image_confirm,
      confirm: () => {
        guideImage.remove(index);
      },
    });
  };

  const onChangeGuideImageDescription = ({ index, value }: { index: number; value: string }) => {
    const field = guideImage.fields[index];
    const { url } = JSON.parse(field.value as string);

    guideImage.update(index, {
      id: field.id,
      order: field.order ?? index + 1,
      value: JSON.stringify({
        url,
        description: value,
      }),
    });
  };

  const onMoveUp = (index: number) => {
    if (index > 0) {
      const currentField = guideImage.fields[index];
      const previousField = guideImage.fields[index - 1];
      
      if (!currentField || !previousField) return;

      // 순서 변경
      guideImage.move(index, index - 1);

      guideImage.update(index - 1, { ...currentField, order: previousField['order'] ?? index });
      guideImage.update(index, { ...previousField, order: currentField['order'] ?? index + 1 });
    }
  };

  const onMoveDown = (index: number) => {
    if (index < guideImage.fields.length - 1) {
      const currentField = guideImage.fields[index];
      const nextField = guideImage.fields[index + 1];
      
      if (!currentField || !nextField) return;

      guideImage.move(index, index + 1);

      guideImage.update(index + 1, { ...currentField, order:nextField['order'] ?? index + 2});
      guideImage.update(index, { ...nextField, order: currentField['order'] ?? index + 1 });
    }
  };


  return (
    <CampaignFormField
      as="div"
      wrapperClassName="!mt-[28px]"
      label={label}
      labelClassName={labelClassName || '!text-[17px]'}
      description={description}
      descriptionClassName={descriptionClassName}
    >
      <div className="flex flex-col gap-[10px]">
        {guideImage.fields.map((field, index) => {
          const { url, description } = JSON.parse(field.value as string);

          return (
            <div key={`guide-image-${index}`} className="flex flex-row gap-[10px]">
             

              {/* 이미지 영역 */}
              <div className="relative w-[33%]">
              <div className="border border-[#E8E8E8]">
                <img src={url} className="object-cover w-full h-full" />

                {/* 이미지 상단 버튼 영역 */}
                <div className="absolute top-0 left-0 right-0 flex flex-row items-stretch" style={{padding: '1px'}}>
                  {/* 이미지 교체하기 버튼 */}
                  <button
                    type="button"
                    className="flex-1 p-1 text-[11px] text-white bg-black bg-opacity-50 hover:bg-opacity-90 cursor-pointer flex items-center justify-center"
                    onClick={() => onReplaceGuideImage(index)}
                  >
                    {languageTexts.create_reels_form_image_modify}
                  </button>

                  {/* 이미지 삭제 버튼 */}
                  <button
                    type="button"
                    className="p-1 bg-black bg-opacity-90 hover:bg-opacity-100 cursor-pointer flex items-center justify-center"
                    style={{marginRight: '1px'}}
                    onClick={() => onDeleteGuideImage(index)}
                  >
                    <img className="w-4 h-4" src={'/assets/icons/close-white.svg'} alt="삭제" />
                  </button>

                  {/* 숨겨진 파일 입력 */}
                  <input
                    ref={(el) => (replaceFileInputRefs.current[index] = el)}
                    className="hidden"
                    accept="image/*"
                    type="file"
                    onChange={(e) => onChangeReplaceGuideImage(e, index)}
                  />
                </div>
              </div>
            {/* 순서 변경 버튼 영역 */}
               {guideImage.fields?.length > 1 && 
               <div className="flex flex-row gap-[1px]">
                <button
                  type="button"
                  className="cursor-pointer disabled:cursor-not-allowed"
                  onClick={() => onMoveUp(index)}
                  disabled={index === 0}
                  aria-label="위로 이동"
                >
                 <ArrowUpIcon 
                    color={index === 0 ? "#9CA3AF" : "black"} 
                  />
                </button>
               <button
                  type="button"
                  className="cursor-pointer disabled:cursor-not-allowed"
                  onClick={() => onMoveDown(index)}
                  disabled={index === guideImage.fields.length - 1}
                  aria-label="아래로 이동"
                >
                   <ArrowDownIcon 
                    color={index === guideImage.fields.length - 1 ? "#9CA3AF" : "black"} 
                  />
                </button>
                
              </div>}
              </div>

              <div className="w-full">
                <TextInputField
                  placeholder={languageTexts.one_line_desc}
                  value={description}
                  onChangeText={(value) => onChangeGuideImageDescription({ index, value })}
                  borderColor={'#E8E8E8'}
                  bordered
                />
              </div>
            </div>
          );
        })}

        {/* 이미지 업로드 버튼 */}
        <button
          type="button"
          className="flex justify-center items-center cursor-pointer mx-auto py-3 w-full border-[1px] border-[#E8E8E8]"
          onClick={onClickUploadGuideImageButton}
        >
          {uploadButtonText}
          <input ref={fileInputRef} className="hidden" accept="image/*" type="file" onChange={onChangeGuideImage} />
        </button>
      </div>
    </CampaignFormField>
  );
};

export default memo(GuideImageField);
